import React, { useEffect, useState } from "react";
import PublicLayout from "../../layouts/PublicLayout";
import Alert from "../../components/Alert";
import {
  publicInstituteList,
  publicClassList,
  publicGroupConfigList,
  getFeeInfo,
  uploadApplicantFile,
  saveApplicantInfo,
  findSingleApplicant,
} from "../../api/onlineAdmissionApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const emptyForm = {
  applicantName: "",
  fatherName: "",
  fatherOccupation: "",
  fatherNid: "",
  motherName: "",
  motherOccupation: "",
  motherNid: "",
  presentAddress: "",
  permanentAddress: "",
  mobileNumber: "",
  dateOfBirth: "",
  religion: "",
  gender: "",
  bloodGroup: "",
  quota: "",
  honoursAdmissionRoll: "",
  hscInstitution: "",
  hscBoard: "",
  hscRoll: "",
  hscRegNo: "",
  hscPassingYear: "",
  hscGpa: "",
  birthCertificateNo: "",
};

/**
 * Public, unauthenticated — digicampusbd's online-admission application
 * form + status-check page (was {@code online-admission.html} /
 * {@code online-applicant-info.html}). See MIGRATION.md Phase 10 notes:
 * this is the one real onlineapplication flow, reached via a super-admin
 * -only hardcoded staff-side sidebar section but a fully public form here,
 * same as the real app.
 */
export default function OnlineAdmissionPage() {
  const [mode, setMode] = useState("apply");

  return (
    <PublicLayout title="Online Admission">
      <div className="public-card">
        <div className="attendance-filters" style={{ marginBottom: 16 }}>
          <button type="button" className={mode === "apply" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("apply")}>
            Apply
          </button>
          <button type="button" className={mode === "status" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("status")}>
            Check Application Status
          </button>
        </div>
        {mode === "apply" ? <ApplyForm /> : <StatusCheck />}
      </div>
    </PublicLayout>
  );
}

function ApplyForm() {
  const [institutes, setInstitutes] = useState([]);
  const [instituteId, setInstituteId] = useState("");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [groupConfigs, setGroupConfigs] = useState([]);
  const [groupConfigId, setGroupConfigId] = useState("");
  const [feeInfo, setFeeInfo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [admissionFile, setAdmissionFile] = useState(null);
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    publicInstituteList().then(setInstitutes);
  }, []);

  useEffect(() => {
    if (!instituteId) {
      setClasses([]);
      return;
    }
    publicClassList(instituteId).then(setClasses);
    setClassId("");
    setGroupConfigId("");
    setGroupConfigs([]);
    setFeeInfo(null);
  }, [instituteId]);

  useEffect(() => {
    if (!classId || !instituteId) {
      setGroupConfigs([]);
      return;
    }
    publicGroupConfigList(classId, instituteId).then(setGroupConfigs);
    setGroupConfigId("");
    setFeeInfo(null);
  }, [classId, instituteId]);

  useEffect(() => {
    if (!groupConfigId) {
      setFeeInfo(null);
      return;
    }
    getFeeInfo(groupConfigId).then(setFeeInfo);
  }, [groupConfigId]);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!instituteId || !groupConfigId) {
      setError("Select an institute, class, and group.");
      return;
    }
    setSubmitting(true);
    try {
      let imageName;
      let admissionFileName;
      let transcriptFileName;
      if (photoFile) {
        const uploaded = await uploadApplicantFile(photoFile);
        imageName = uploaded.fileName;
      }
      if (admissionFile) {
        const uploaded = await uploadApplicantFile(admissionFile);
        admissionFileName = uploaded.fileName;
      }
      if (transcriptFile) {
        const uploaded = await uploadApplicantFile(transcriptFile);
        transcriptFileName = uploaded.fileName;
      }

      const res = await saveApplicantInfo({
        ...form,
        instituteId: Number(instituteId),
        groupConfigId: Number(groupConfigId),
        imageName,
        admissionFileName,
        transcriptFileName,
      });
      setResult(res.data);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Alert type="success">
        Application submitted successfully. Your registration number is <strong>{result}</strong>. Please note this down — you'll
        need it (with the institute) to check your application status and pay the application fee.
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="user-form">
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>

      <label>Institute</label>
      <select value={instituteId} onChange={(e) => setInstituteId(e.target.value)} required>
        <option value="">Select institute</option>
        {institutes.map((i) => (
          <option key={i.id} value={i.id}>
            {i.name}
          </option>
        ))}
      </select>

      <label>Class</label>
      <select value={classId} onChange={(e) => setClassId(e.target.value)} required disabled={!instituteId}>
        <option value="">Select class</option>
        {classes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      <label>Group</label>
      <select value={groupConfigId} onChange={(e) => setGroupConfigId(e.target.value)} required disabled={!classId}>
        <option value="">Select group</option>
        {groupConfigs.map((g) => (
          <option key={g.id} value={g.id}>
            {g.groupName}
          </option>
        ))}
      </select>
      {feeInfo && (
        <p style={{ fontSize: "0.85rem", color: "#374151" }}>
          Application Fee: {feeInfo.applicationFee || 0} + Service Charge: {feeInfo.serviceCharge || 0} = Total:{" "}
          <strong>{feeInfo.totalFee || 0}</strong>
        </p>
      )}

      <label>Applicant Name</label>
      <input value={form.applicantName} onChange={setField("applicantName")} required />

      <label>Date of Birth</label>
      <input type="date" value={form.dateOfBirth} onChange={setField("dateOfBirth")} />

      <label>Gender</label>
      <select value={form.gender} onChange={setField("gender")}>
        <option value="">-</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
      </select>

      <label>Religion</label>
      <input value={form.religion} onChange={setField("religion")} />

      <label>Blood Group</label>
      <input value={form.bloodGroup} onChange={setField("bloodGroup")} />

      <label>Mobile Number</label>
      <input value={form.mobileNumber} onChange={setField("mobileNumber")} required />

      <label>Father Name</label>
      <input value={form.fatherName} onChange={setField("fatherName")} required />

      <label>Father Occupation</label>
      <input value={form.fatherOccupation} onChange={setField("fatherOccupation")} />

      <label>Mother Name</label>
      <input value={form.motherName} onChange={setField("motherName")} required />

      <label>Mother Occupation</label>
      <input value={form.motherOccupation} onChange={setField("motherOccupation")} />

      <label>Present Address</label>
      <textarea rows={2} value={form.presentAddress} onChange={setField("presentAddress")} />

      <label>Permanent Address</label>
      <textarea rows={2} value={form.permanentAddress} onChange={setField("permanentAddress")} />

      <label>Quota (if any)</label>
      <input value={form.quota} onChange={setField("quota")} />

      <label>Honours Admission Roll (if applicable)</label>
      <input value={form.honoursAdmissionRoll} onChange={setField("honoursAdmissionRoll")} />

      <h4>HSC Information</h4>
      <label>HSC Institution</label>
      <input value={form.hscInstitution} onChange={setField("hscInstitution")} />
      <label>HSC Board</label>
      <input value={form.hscBoard} onChange={setField("hscBoard")} />
      <label>HSC Roll</label>
      <input value={form.hscRoll} onChange={setField("hscRoll")} />
      <label>HSC Registration No.</label>
      <input value={form.hscRegNo} onChange={setField("hscRegNo")} />
      <label>HSC Passing Year</label>
      <input value={form.hscPassingYear} onChange={setField("hscPassingYear")} />
      <label>HSC GPA</label>
      <input value={form.hscGpa} onChange={setField("hscGpa")} />

      <h4>Documents</h4>
      <label>Photo</label>
      <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} />
      <label>Admission Roll Proof (optional)</label>
      <input type="file" onChange={(e) => setAdmissionFile(e.target.files[0])} />
      <label>HSC Transcript (optional)</label>
      <input type="file" onChange={(e) => setTranscriptFile(e.target.files[0])} />

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </div>
    </form>
  );
}

function StatusCheck() {
  const [registrationId, setRegistrationId] = useState("");
  const [instituteId, setInstituteId] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    publicInstituteList().then(setInstitutes);
  }, []);

  const handleCheck = async (e) => {
    e.preventDefault();
    setError("");
    setData(null);
    setLoading(true);
    try {
      const res = await findSingleApplicant(registrationId, instituteId);
      setData(res);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "No application found.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleCheck} className="user-form">
        <Alert type="error" onDismiss={() => setError("")}>
          {error}
        </Alert>
        <label>Institute</label>
        <select value={instituteId} onChange={(e) => setInstituteId(e.target.value)} required>
          <option value="">Select institute</option>
          {institutes.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </select>
        <label>Registration Number</label>
        <input value={registrationId} onChange={(e) => setRegistrationId(e.target.value)} required />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Checking..." : "Check Status"}
          </button>
        </div>
      </form>

      {data && (
        <div style={{ marginTop: 16 }}>
          <h3>{data.instituteName}</h3>
          <p>{data.instituteAddress}</p>
          <div className="table-scroll">
          <table className="attendance-table">
            <tbody>
              <tr>
                <th>Registration No</th>
                <td>{data.registrationId}</td>
              </tr>
              <tr>
                <th>Applicant Name</th>
                <td>{data.applicantName}</td>
              </tr>
              <tr>
                <th>Class (Group)</th>
                <td>
                  {data.className} ({data.groupName})
                </td>
              </tr>
              <tr>
                <th>Application Date</th>
                <td>{data.applicationDate}</td>
              </tr>
              <tr>
                <th>Payment Status</th>
                <td>{data.paymentStatus}</td>
              </tr>
              <tr>
                <th>Application Status</th>
                <td>{data.applicantStatus}</td>
              </tr>
            </tbody>
          </table>
          </div>
          <Alert type={data.paymentStatus === "Paid" ? "success" : "error"}>{data.paymentMessage}</Alert>
        </div>
      )}
    </>
  );
}
